import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Plus, Pencil, Trash2, X, Users, User } from "lucide-react";

import { API_URL as API } from "../../config";
const COLORS = ["#87CEEB", "#F87171", "#34D399", "#FBBF24", "#A78BFA", "#FB923C", "#38BDF8", "#F472B6"];

export default function TeamsPage() {
  const { authFetch } = useAuth();
  const { t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", color: "#87CEEB", athlete_ids: [] });
  const [editId, setEditId] = useState(null);
  const a = t.admin;

  const load = useCallback(async () => {
    const [tRes, aRes] = await Promise.all([authFetch(`${API}/api/admin/teams`), authFetch(`${API}/api/admin/athletes`)]);
    if (tRes.ok) setTeams(await tRes.json());
    if (aRes.ok) setAthletes(await aRes.json());
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const athleteMap = {};
  athletes.forEach(at => { athleteMap[at.id] = at; });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    const url = editId ? `${API}/api/admin/teams/${editId}` : `${API}/api/admin/teams`;
    await authFetch(url, { method: editId ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setShowForm(false); setForm({ name: "", color: "#87CEEB", athlete_ids: [] }); setEditId(null); load();
  };

  const handleEdit = (tm) => {
    setForm({ name: tm.name, color: tm.color || "#87CEEB", athlete_ids: tm.athlete_ids || [] });
    setEditId(tm.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`${a.delete}?`)) return;
    await authFetch(`${API}/api/admin/teams/${id}`, { method: "DELETE" }); load();
  };

  const toggleAthlete = (id) => {
    setForm(prev => ({ ...prev, athlete_ids: prev.athlete_ids.includes(id) ? prev.athlete_ids.filter(x => x !== id) : [...prev.athlete_ids, id] }));
  };

  return (
    <div data-testid="teams-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div><h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{a.teams}</h2>
          <p className="text-gray-500 text-sm">{teams.length} {a.teams.toLowerCase()}</p></div>
        <button onClick={() => { setForm({ name: "", color: "#87CEEB", athlete_ids: [] }); setEditId(null); setShowForm(true); }} data-testid="add-team-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> {a.createTeam}
        </button>
      </div>

      {teams.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{a.noTeams}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <div key={team.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-all" data-testid={`team-card-${team.id}`}>
              <div className="h-2" style={{ backgroundColor: team.color || "#87CEEB" }} />
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold font-['Kanit']">{team.name}</h3>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(team)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-500"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(team.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-3">{(team.athlete_ids || []).length} {a.players}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(team.athlete_ids || []).slice(0, 8).map(aid => {
                    const at = athleteMap[aid];
                    return at ? (
                      <span key={aid} className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-xs">
                        <User className="w-3 h-3" />{at.name.split(" ")[0]}
                      </span>
                    ) : null;
                  })}
                  {(team.athlete_ids || []).length > 8 && <span className="text-xs text-gray-400 px-2 py-1">+{team.athlete_ids.length - 8} more</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="team-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{editId ? a.editTeam : a.createTeam}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.teamName} *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="team-name-input" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{a.teamColor}</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setForm({...form, color: c})}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? "border-gray-800 scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">{a.athletes} ({form.athlete_ids.length} {a.selected})</label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl divide-y">
                  {athletes.filter(at => at.status === "active").map(at => (
                    <label key={at.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={form.athlete_ids.includes(at.id)} onChange={() => toggleAthlete(at.id)} className="rounded border-gray-300" />
                      <span className="text-sm">{at.name} <span className="text-gray-400">#{at.jersey_number || "-"}</span></span>
                    </label>
                  ))}
                  {athletes.filter(at => at.status === "active").length === 0 && <p className="px-3 py-4 text-center text-gray-400 text-sm">{a.noActiveAthletes}</p>}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="team-form-submit" className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{editId ? a.update : a.createTeam}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
